// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import "./styles/main.scss";
import { AuthProvider } from './context/AuthContext';
import AppContent from './AppContent';
import RouteChangeTracker from './components/RouteChangeTracker';
import SyncDialogNew from './components/jobsite-management/SyncDialogNew';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { authService } from './services/auth.service';
import { useOfflineDataStatus } from './hooks/useOfflineDataStatus';

const App: React.FC = () => {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
};

const AppWrapper: React.FC = () => {
  const [showSyncDialog, setShowSyncDialog] = React.useState(false);
  const [syncStatus, setSyncStatus] = React.useState('');
  const [syncInProgress, setSyncInProgress] = React.useState(false);
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();
  const { hasOfflineData, isLoading, refreshOfflineStatus } = useOfflineDataStatus();
  
  React.useEffect(() => {
    // Only run this effect when both network status and offline data check are complete
    if (!isLoading && isOnline && hasOfflineData && authService.isAuthenticated() && !syncInProgress) {
      setShowSyncDialog(true);
    }
  }, [isOnline, hasOfflineData, isLoading, syncInProgress]);

  // Separate effect to handle dialog closing after sync completes
  React.useEffect(() => {
    if (syncStatus === 'COMPLETED') {
      // Add a delay to ensure the completed message is shown before closing
      const timer = setTimeout(() => {
        setShowSyncDialog(false);
      }, 3000); // 3 second delay
      
      return () => clearTimeout(timer);
    }
  }, [syncStatus]);

  const handleSync = (syncStatus: string, syncProgress: number, defaultSyncMessage: string) => {
    setSyncStatus(syncStatus);
    
    if (syncStatus === 'IN_PROGRESS') {
      setSyncInProgress(true);
    } else if (syncStatus === 'COMPLETED' || syncStatus === "") {
      // Refresh offline data status after sync completes
      
      refreshOfflineStatus();
      setSyncInProgress(false);
      
      if(window.location.pathname !== '/jobsite-management') {
        // Use setTimeout to ensure the completed message is shown before navigating
        setTimeout(() => {
          navigate('/jobsite-management');
        }, 2500);
      } else if(window.location.pathname === '/jobsite-management')   {
        window.location.reload();
      }
    }
  }

  const handleSyncDialogClose = () => {
    setShowSyncDialog(false);
    if (syncStatus === 'COMPLETED') {
      setSyncStatus('');
    }
  }

  return (
    <AuthProvider>
      <RouteChangeTracker />
      <AppContent />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {<SyncDialogNew
        isOpen={isOnline && showSyncDialog}
        onClose={handleSyncDialogClose}
        handleSync={handleSync}
        isOnline={isOnline}
      />}
    </AuthProvider>
  );
};

export default App;
